/**
 * Specialized SearchInput component with search icon and clear functionality
 */

import React, { useState, useCallback } from 'react';
import { InputProps } from './Input';
import { Input } from './Input';
import { IconButton } from '../buttons/IconButton';
import { FiSearch, FiX } from 'react-icons/fi';
import { InputRightElement, InputGroup } from '@chakra-ui/react';

export interface SearchInputProps
  extends Omit<InputProps, 'leftIcon' | 'rightIcon' | 'type'> {
  /** Callback when search is performed */
  onSearch?: (value: string) => void;
  /** Callback when input is cleared */
  onClear?: () => void;
  /** Whether to show clear button when input has value */
  showClearButton?: boolean;
  /** Debounce delay for search in milliseconds */
  debounceMs?: number;
}

export const SearchInput: React.FC<SearchInputProps> = ({
  onSearch,
  onClear,
  showClearButton = true,
  debounceMs = 300,
  value: controlledValue,
  onChange,
  placeholder = 'Search...',
  ...props
}) => {
  const [internalValue, setInternalValue] = useState('');
  const [searched, setSearched] = useState(false);

  const value = controlledValue !== undefined ? controlledValue : internalValue;
  const hasValue = Boolean(value);

  const performSearch = useCallback(() => {
    onSearch?.(value as string);
    setSearched(true);
  }, [onSearch]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      if (controlledValue === undefined) {
        setInternalValue(newValue);
      }
      onChange?.(e);
    },
    [controlledValue, onChange],
  );

  const handleClear = useCallback(() => {
    if (controlledValue === undefined) {
      setInternalValue('');
    }

    onClear?.();
    onSearch?.('');
    setSearched(false);
  }, [controlledValue, onClear, onSearch]);

  return (
    <InputGroup size={props.size || 'md'}>
      <Input
        {...props}
        type="search"
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
      />
      {hasValue && (
        <InputRightElement>
          <IconButton
            icon={searched ? <FiX /> : <FiSearch />}
            label={searched ? 'Clear' : 'Search'}
            variant="secondary"
            size="sm"
            onClick={searched ? handleClear : performSearch}
          />
        </InputRightElement>
      )}
    </InputGroup>
  );
};
